import React from 'react';
import { Box, makeStyles, Typography, useTheme } from '@material-ui/core';
import { Person } from '@material-ui/icons';
import { IClientCardModel } from 'interfaces';
import SensitiveValue from 'components/SensitiveValue';
import { BORDER_COLOR, PILL_BLUE_BG, PILL_GREEN, Pill, SECONDARY_COLOR, useCardStyles } from './helpers';

interface IProps {
  card: IClientCardModel;
  phone: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    backgroundColor: '#fff',
    border: `1px solid ${BORDER_COLOR}`,
    borderRadius: 12,
    padding: 24,
    marginBottom: theme.spacing(2),
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: PILL_BLUE_BG,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 20,
    flexShrink: 0,
    marginRight: 20,
  },
  main: {
    flexGrow: 1,
    minWidth: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  fio: {
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1.3,
    marginRight: 10,
  },
  pill: {
    marginRight: 8,
  },
  statusEmpty: {
    fontSize: 13,
    color: SECONDARY_COLOR,
    marginRight: 12,
  },
  iinRow: {
    display: 'flex',
    alignItems: 'center',
  },
  counters: {
    display: 'flex',
    marginLeft: 20,
  },
  counter: {
    width: 104,
    boxSizing: 'border-box',
    padding: '12px 14px',
    border: `1px solid ${BORDER_COLOR}`,
    borderRadius: 8,
    '& + &': {
      marginLeft: 12,
    },
  },
  counterValue: {
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1.3,
  },
}));

// Инициалы — первые буквы двух первых слов ФИО.
const getInitials = (fio: string | null): string =>
  (fio || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

/** Шапка карточки клиента: ФИО, статусы, ИИН и счётчики объектов/договоров. */
const ClientCardHeader: React.FC<IProps> = ({ card, phone }) => {
  const classes = useStyles();
  const cardClasses = useCardStyles();
  const theme = useTheme();
  const initials = getInitials(card.fio);

  const renderCounter = (label: string, value: number) => (
    <Box className={classes.counter}>
      <Typography className={cardClasses.fieldLabel}>{label}</Typography>
      <Typography className={classes.counterValue}>{value}</Typography>
    </Box>
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.avatar}>{initials || <Person />}</Box>
      <Box className={classes.main}>
        <Box className={classes.titleRow}>
          <Typography component="h2" className={classes.fio}>
            {card.fio || '-'}
          </Typography>
          {/* отсутствующий статус — «-», как в прежней карточке */}
          {card.biClubCardType ? (
            <Pill
              className={classes.pill}
              label={`BI Club · ${card.biClubCardType}`}
              colors={{ bg: PILL_BLUE_BG, color: theme.palette.primary.main }}
            />
          ) : (
            <Typography component="span" className={classes.statusEmpty}>
              BI Club: -
            </Typography>
          )}
          {card.isEmployee ? (
            <Pill className={classes.pill} label="Сотрудник BI" colors={PILL_GREEN} />
          ) : (
            <Typography component="span" className={classes.statusEmpty}>
              Сотрудник BI: -
            </Typography>
          )}
        </Box>
        <Box className={classes.iinRow}>
          <Typography className={cardClasses.fieldLabel} style={{ marginRight: 6 }}>
            ИИН
          </Typography>
          <Box className={cardClasses.fieldValue}>
            <SensitiveValue masked={card.iin} phone={phone} field="IIN" />
          </Box>
        </Box>
      </Box>
      <Box className={classes.counters}>
        {renderCounter('Объекты', (card.objects || []).length)}
        {renderCounter('Договоры', (card.contracts || []).length)}
      </Box>
    </Box>
  );
};

export default ClientCardHeader;
